import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Zap, TrendingDown, ShieldCheck, ArrowRight, CheckCircle2 } from 'lucide-react'
import api from '../../api/client'

const LOGO_FILTER = 'brightness(0) saturate(100%) invert(8%)'
const inp = 'w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 outline-none focus:border-accent-500 focus:bg-white transition'
const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })

export default function Simulador() {
  const { codigo } = useParams()
  const [sponsor, setSponsor] = useState(null)
  const [dists, setDists] = useState([])
  const [estado, setEstado] = useState('')
  const [f, setF] = useState({ nome: '', whatsapp: '', cidade: '', distribuidora_id: '', valor_conta: '' })
  const [res, setRes] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const [invalido, setInvalido] = useState(false)

  useEffect(() => {
    api.get(`/simulacoes/publico/sponsor/${codigo}`)
      .then(({ data }) => { setSponsor(data.licenciado); setDists(data.distribuidoras || []) })
      .catch(() => setInvalido(true))
  }, [codigo])

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const distFiltradas = estado ? dists.filter((d) => d.estado === estado) : dists
  const estados = [...new Set(dists.map((d) => d.estado))].sort()

  async function simular(e) {
    e.preventDefault()
    setErr('')
    if (!f.nome || !f.whatsapp || !f.distribuidora_id || !f.valor_conta) return setErr('Preencha nome, WhatsApp, distribuidora e valor da conta.')
    setLoading(true)
    try {
      const d = dists.find((x) => String(x.id) === String(f.distribuidora_id))
      const { data } = await api.post(`/simulacoes/publico/${codigo}`, {
        nome: f.nome, whatsapp: f.whatsapp, celular: f.whatsapp, cidade: f.cidade,
        estado: estado || (d ? d.estado : ''), distribuidora_id: Number(f.distribuidora_id),
        distribuidora_nome: d ? d.nome : '', valor_conta: Number(f.valor_conta),
      })
      setRes(data)
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Não foi possível simular agora.') } finally { setLoading(false) }
  }

  if (invalido) {
    return <div className="min-h-screen flex items-center justify-center bg-offwhite p-6">
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-8 text-center max-w-md">
        <h2 className="text-lg font-heading font-extrabold text-graphite">Link inválido</h2>
        <p className="text-sm text-gray-600 mt-2">Este link de simulação não é válido. Solicite um novo ao seu consultor The Conect.</p>
      </div>
    </div>
  }

  return (
    <div className="min-h-screen bg-offwhite">
      {/* Header */}
      <div className="px-6 py-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #ffcc00 0%, #ff9d33 50%, #ff6c00 100%)' }}>
        <img src="/logothe.png" alt="The Conect" style={{ maxHeight: 34, filter: LOGO_FILTER }} />
        <span className="text-graphite/80 text-sm font-semibold ml-auto">{sponsor ? `Consultor: ${sponsor}` : ''}</span>
      </div>

      <div className="max-w-lg mx-auto p-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full mb-3 text-xs font-bold uppercase tracking-wider" style={{ background: 'linear-gradient(135deg,#fff5cc,#ffe4bf)', color: '#b34c00' }}>
            <Zap size={12} /> Energia por Assinatura
          </div>
          <h1 className="text-2xl font-heading font-extrabold text-graphite">Simule sua economia na conta de luz</h1>
          <p className="text-sm text-gray-500 mt-1">Sem obras, sem troca de fiação. Veja quanto você economiza por mês.</p>
        </div>

        {!res ? (
          <form onSubmit={simular} className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 space-y-3">
            {err && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}
            <div><label className="block text-sm font-semibold text-graphite mb-1">Seu nome</label><input className={inp} value={f.nome} onChange={set('nome')} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-semibold text-graphite mb-1">WhatsApp</label><input className={inp} value={f.whatsapp} onChange={set('whatsapp')} placeholder="(00) 00000-0000" /></div>
              <div><label className="block text-sm font-semibold text-graphite mb-1">Cidade</label><input className={inp} value={f.cidade} onChange={set('cidade')} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-semibold text-graphite mb-1">Estado</label>
                <select className={inp} value={estado} onChange={(e) => { setEstado(e.target.value); setF((s) => ({ ...s, distribuidora_id: '' })) }}>
                  <option value="">Todos</option>{estados.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </select></div>
              <div><label className="block text-sm font-semibold text-graphite mb-1">Distribuidora</label>
                <select className={inp} value={f.distribuidora_id} onChange={set('distribuidora_id')}>
                  <option value="">Selecione</option>{distFiltradas.map((d) => <option key={d.id} value={d.id}>{d.nome} ({d.estado})</option>)}
                </select></div>
            </div>
            <div><label className="block text-sm font-semibold text-graphite mb-1">Valor médio da conta (R$)</label><input type="number" className={inp} value={f.valor_conta} onChange={set('valor_conta')} /></div>
            <button disabled={loading} className="w-full rounded-xl text-white font-bold py-3 disabled:opacity-60 hover:brightness-105 transition inline-flex items-center justify-center gap-2" style={{ background: 'linear-gradient(135deg,#ffcc00,#ff6c00)' }}>
              {loading ? 'Simulando...' : 'Simular economia'} <ArrowRight size={16} />
            </button>
            <p className="text-center text-[11px] text-gray-400 flex items-center justify-center gap-1"><ShieldCheck size={12} className="text-accent-500" /> Seus dados são tratados com segurança (LGPD).</p>
          </form>
        ) : (
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden">
            <div className="h-1.5" style={{ background: 'linear-gradient(90deg,#ffcc00,#ff6c00)' }} />
            <div className="p-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3"><TrendingDown className="text-emerald-500" size={26} /></div>
              <p className="text-sm text-gray-500">Sua economia estimada</p>
              <p className="text-4xl font-heading font-extrabold text-emerald-600 my-1">{money(res.economia_mensal)}<span className="text-sm text-gray-400 font-normal">/mês</span></p>
              <p className="text-sm text-gray-600">Cerca de <b>{money(res.economia_anual)}</b> por ano · desconto de <b>{res.percentual_desconto}%</b></p>
              <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
                <div className="bg-neutral-50 rounded-xl p-3"><p className="text-[11px] text-gray-400">Conta hoje</p><p className="font-bold text-graphite">{money(f.valor_conta)}</p></div>
                <div className="bg-emerald-50 rounded-xl p-3"><p className="text-[11px] text-emerald-600">Novo valor</p><p className="font-bold text-emerald-700">{money(res.novo_valor)}</p></div>
              </div>
              <div className="mt-5 flex items-center justify-center gap-1.5 text-sm text-graphite"><CheckCircle2 size={16} className="text-emerald-500" /> {sponsor} vai entrar em contato pelo seu WhatsApp.</div>
              <a href={`https://wa.me/?text=${encodeURIComponent('Quero contratar a economia de energia da The Conect!')}`} target="_blank" rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-xl text-white font-bold px-6 py-3 hover:brightness-105 transition" style={{ background: 'linear-gradient(135deg,#25D366,#128C7E)' }}>
                Falar no WhatsApp <ArrowRight size={16} />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
